import { useEffect, useState } from "react";
import { Undo2, Plus, Trash2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const CATEGORY_COLORS = ["#e11d48", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#94a3b8"];

export const CategoryDots = ({ ids = [], categories }) => {
  const cats = categories.filter((c) => ids.includes(c.id));
  if (!cats.length) return null;
  return (
    <span className="inline-flex items-center gap-1 mr-1.5 shrink-0" data-testid="category-dots">
      {cats.slice(0, 4).map((c) => (
        <span key={c.id} title={c.name} className="h-2 w-2 rounded-full inline-block" style={{ background: c.color }} />
      ))}
    </span>
  );
};

const popStyle = (pos) => ({
  left: Math.min(pos.x, window.innerWidth - 300),
  top: Math.min(pos.y, window.innerHeight - 360),
});

export const SnoozePopover = ({ pos, onPick, onClose }) => {
  const [custom, setCustom] = useState("");
  const at = (d) => d.toISOString();
  const today = new Date();
  const presets = [
    ["Later today (+3 hours)", () => at(new Date(Date.now() + 3 * 3600e3))],
    ["This evening 6 PM", () => { const d = new Date(); d.setHours(18, 0, 0, 0); return at(d); }],
    ["Tomorrow 9 AM", () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return at(d); }],
    ["Next Monday 9 AM", () => { const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); d.setHours(9, 0, 0, 0); return at(d); }],
  ];
  return (
    <>
      <div data-lenis-prevent className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="fixed z-[61] w-72 bg-nx-navy2 border border-white/15 shadow-2xl p-3" style={popStyle(pos)} data-testid="snooze-popover">
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 px-1 pb-2">Snooze until…</p>
        {presets.map(([label, fn]) => (
          <button key={label} onClick={() => onPick(fn())}
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/[0.05] hover:text-white transition-colors"
            data-testid={`snooze-preset-${label.split(" ")[0].toLowerCase()}`}>{label}</button>
        ))}
        <div className="border-t border-white/10 mt-2 pt-2 flex gap-2 items-center">
          <input type="datetime-local" value={custom} min={new Date(today.getTime() + 5 * 60e3).toISOString().slice(0, 16)}
            onChange={(e) => setCustom(e.target.value)}
            className="flex-1 bg-white/[0.04] border border-white/15 text-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:border-nx-goldlight/70"
            data-testid="snooze-custom-input" />
          <button disabled={!custom} onClick={() => onPick(new Date(custom).toISOString())}
            className="text-[10px] tracking-[0.1em] uppercase bg-nx-gold text-nx-navy font-semibold px-3 py-1.5 disabled:opacity-40"
            data-testid="snooze-custom-go">Set</button>
        </div>
      </div>
    </>
  );
};

export const CategoryPopover = ({ pos, api, messageId, categories, assigned = [], onChanged, onCategoriesChanged, onClose }) => {
  const [sel, setSel] = useState(new Set(assigned));
  const [name, setName] = useState("");
  const [color, setColor] = useState(CATEGORY_COLORS[0]);
  const [busy, setBusy] = useState(false);

  const toggle = async (id) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    setSel(next);
    try {
      await api.post("/mail/extras/categories/assign", { message_id: messageId, category_ids: [...next] });
      onChanged(messageId, [...next]);
    } catch { toast.error("Could not update tags"); }
  };

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/mail/extras/categories", { name: name.trim(), color });
      onCategoriesChanged([...categories, data.category]);
      setName("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create category"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/mail/extras/categories/${id}`);
      onCategoriesChanged(categories.filter((c) => c.id !== id));
    } catch { toast.error("Could not delete category"); }
  };

  return (
    <>
      <div data-lenis-prevent className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="fixed z-[61] w-72 bg-nx-navy2 border border-white/15 shadow-2xl p-3" style={popStyle(pos)} data-testid="category-popover">
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 px-1 pb-2">Categories</p>
        <div className="max-h-44 overflow-y-auto">
          {categories.length === 0 && <p className="px-2 py-2 text-xs text-slate-500">No categories yet — create one below.</p>}
          {categories.map((c) => (
            <div key={c.id} className="group flex items-center gap-2 px-2 py-1.5 hover:bg-white/[0.04]">
              <button onClick={() => toggle(c.id)} className="flex items-center gap-2 flex-1 text-left" data-testid={`category-toggle-${c.id}`}>
                <span className="h-4 w-4 rounded-sm flex items-center justify-center" style={{ background: c.color }}>
                  {sel.has(c.id) && <Check size={11} className="text-white" />}
                </span>
                <span className="text-sm text-slate-200 truncate">{c.name}</span>
              </button>
              <button onClick={() => remove(c.id)} className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-red-400 transition-all" title="Delete category" data-testid={`category-delete-${c.id}`}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 mt-2 pt-2">
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" maxLength={30}
              className="flex-1 bg-white/[0.04] border border-white/15 text-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:border-nx-goldlight/70"
              data-testid="category-new-name" />
            <button disabled={busy || !name.trim()} onClick={create}
              className="text-nx-navy bg-nx-gold px-2.5 disabled:opacity-40" data-testid="category-create">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            </button>
          </div>
          <div className="flex gap-1.5 mt-2 px-0.5">
            {CATEGORY_COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)}
                className={`h-5 w-5 rounded-full transition-transform ${color === c ? "scale-110 ring-2 ring-white/70" : "hover:scale-110"}`}
                style={{ background: c }} aria-label={`Colour ${c}`} data-testid={`category-color-${c.slice(1)}`} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export const UndoSendBar = ({ undo, onUndo, onDone }) => {
  const [left, setLeft] = useState(10);
  useEffect(() => {
    const started = Date.now();
    const t = setInterval(() => {
      const rem = 10 - Math.floor((Date.now() - started) / 1000);
      if (rem <= 0) { clearInterval(t); onDone(); } else setLeft(rem);
    }, 250);
    return () => clearInterval(t);
  }, [undo.sid]); // eslint-disable-line
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-4 bg-nx-navy2 border border-nx-gold/40 shadow-2xl px-5 py-3" data-testid="undo-send-bar">
      <div className="flex flex-col">
        <span className="text-sm text-slate-200">Sending in {left}s…</span>
        <span className="mt-1.5 h-1 w-40 bg-white/10 overflow-hidden rounded-full">
          <span className="block h-full bg-nx-gold transition-all duration-300 rounded-full" style={{ width: `${left * 10}%` }} />
        </span>
      </div>
      <button onClick={onUndo} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-nx-goldlight hover:text-nx-gold font-semibold transition-colors" data-testid="undo-send-button">
        <Undo2 size={14} /> Undo
      </button>
    </div>
  );
};
